import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BellIcon, EnvelopeSimpleIcon, AppWindowIcon } from "@phosphor-icons/react";

/**
 * NotificationSettings — let users toggle in-app + email notifications by
 * category. Critical account/money/moderation notifications stay always-on
 * (we explain why on the page so users don't think it's a bug).
 */
export default function NotificationSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    let alive = true;
    api
      .get("/me/notification-prefs")
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch((e) => toast.error(apiError(e)));
    return () => {
      alive = false;
    };
  }, [user, authLoading, navigate]);

  const toggle = (key) => {
    setData((d) => {
      const currentlyOn = !!d.prefs.in_app?.[key];
      const next = !currentlyOn;
      return {
        ...d,
        prefs: {
          ...d.prefs,
          in_app: { ...d.prefs.in_app, [key]: next },
          email: { ...d.prefs.email, [key]: next },
        },
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/notification-prefs", data.prefs);
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-14 text-zinc-500">Loading…</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="notification-settings-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <p className="overline text-zinc-500">Settings</p>
        <div className="flex items-center gap-3 mt-1">
          <BellIcon size={28} weight="fill" className="text-[#1D78AB]" />
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-zinc-950">
            Notifications.
          </h1>
        </div>
        <p className="mt-3 text-zinc-600 max-w-xl leading-relaxed">
          Pick what reaches you, and where. Critical account, payment, and moderation
          notifications are always on — those are required for trust and safety.
        </p>

        <div className="mt-10 border border-zinc-200 rounded-2xl overflow-hidden bg-white">
          {/* Header row */}
          <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-5 items-center px-5 py-3 bg-zinc-50 border-b border-zinc-200 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
            <div>Category</div>
            <div>Notify me</div>
            <div className="text-right">Try it</div>
          </div>
          {data.categories.map((cat) => (
            <div
              key={cat.key}
              className="grid grid-cols-[1fr_auto_auto] gap-3 sm:gap-5 items-center px-5 py-4 border-b border-zinc-100 last:border-b-0"
              data-testid={`notif-row-${cat.key}`}
            >
              <div className="text-sm text-zinc-900">{cat.label}</div>
              <Toggle
                on={!!data.prefs.in_app?.[cat.key]}
                onChange={() => toggle(cat.key)}
                testid={`notif-toggle-${cat.key}`}
                aria-label={`Notifications for ${cat.key}`}
              />
              <TestButton category={cat.key} />
            </div>
          ))}
        </div>

        <div className="mt-3 text-xs text-zinc-500 leading-relaxed">
          One switch controls both <AppWindowIcon size={11} className="inline mb-0.5 mr-0.5" />
          in-app and <EnvelopeSimpleIcon size={11} className="inline mb-0.5 mr-0.5" />
          email for that category. Off means we'll stay completely quiet on that topic.
        </div>

        <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
          <strong className="text-zinc-700">Always-on (cannot be disabled):</strong>{" "}
          ID verification decisions · order paid / shipped / delivered · payouts ·
          referral credit · review received · moderation flags. These are required
          for trust and safety on the platform.
        </div>

        <div className="mt-8 flex justify-between items-center pt-4 border-t border-zinc-200">
          <Link to="/dashboard" className="text-sm text-zinc-600 hover:underline">
            ← Back to dashboard
          </Link>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#1D78AB] hover:bg-[#165d85] text-white h-11 px-7 rounded-full"
            data-testid="save-notif-prefs-btn"
          >
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Toggle({ on, onChange, testid, ...rest }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onChange}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        on ? "bg-[#1D78AB]" : "bg-zinc-300"
      }`}
      {...rest}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          on ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

/**
 * TestButton — fires a sample notification for one category. Surfaces
 * the muted-state explicitly so users immediately see when a toggle is
 * doing its job ("Muted — open the bell icon and you'll see nothing.").
 */
function TestButton({ category }) {
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    // Prevent the click bubbling up to any parent <form>.
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post("/me/notification-prefs/test", { category });
      if (r.data?.sent) {
        toast.success("Sample sent — check the bell icon up top.");
      } else {
        toast(`Muted — that's exactly what your toggle is supposed to do.`, {
          icon: "🔇",
        });
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={send}
      disabled={busy}
      className="text-[11px] font-bold text-[#1D78AB] hover:underline disabled:opacity-50 px-2 py-1"
      data-testid={`notif-test-btn-${category}`}
    >
      {busy ? "…" : "Test"}
    </button>
  );
}
